import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Users, Sparkles, Loader2, ChevronRight, Crown,
  Building2, PenTool, Edit3, CheckCircle, Layers,
  Shield, Palette, Search, MessageSquare, Workflow,
  Brain, TrendingUp, Lightbulb
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Agent icons mapping
const AGENT_ICONS = {
  architect: Building2,
  ghostwriter: PenTool,
  editor: Edit3,
  reviewer: CheckCircle,
  structurer: Layers,
  curator: Shield,
  designer: Palette,
  researcher: Search,
};

// Workflow descriptions
const WORKFLOWS = [
  {
    id: 'quick_polish',
    name: 'Polimento Rápido',
    description: 'Editor + Curador para refinamento rápido',
    agents: ['editor', 'curator'],
    premium: false
  },
  {
    id: 'structure_only',
    name: 'Apenas Estrutura',
    description: 'Arquiteto + Estruturador para organização',
    agents: ['architect', 'structurer'],
    premium: false
  },
  {
    id: 'content_generation',
    name: 'Gerar Conteúdo',
    description: 'Fluxo completo de criação de conteúdo',
    agents: ['architect', 'ghostwriter', 'editor', 'designer'],
    premium: false
  },
  {
    id: 'full_edit',
    name: 'Edição Completa',
    description: 'Todos os agentes em sequência',
    agents: ['architect', 'structurer', 'editor', 'reviewer', 'curator', 'designer'],
    premium: true
  },
  {
    id: 'research_and_write',
    name: 'Pesquisar e Escrever',
    description: 'Pesquisa + Escrita + Revisão',
    agents: ['researcher', 'ghostwriter', 'editor', 'curator'],
    premium: true
  },
  {
    id: 'deep_review',
    name: 'Revisão Profunda',
    description: 'Análise detalhada de consistência',
    agents: ['reviewer', 'structurer', 'editor', 'curator'],
    premium: true
  }
];

export default function ConselhoAgentes() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  
  // Tabs
  const [activeTab, setActiveTab] = useState('agents'); // agents, workflows, consult, memory
  
  // Workflow state
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowContent, setWorkflowContent] = useState('');
  const [workflowResult, setWorkflowResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Consult state
  const [question, setQuestion] = useState('');
  const [questionContext, setQuestionContext] = useState('');
  const [councilResponse, setCouncilResponse] = useState(null);
  
  // Memory state
  const [authorProfile, setAuthorProfile] = useState(null);
  const [suggestions, setSuggestions] = useState(null);

  useEffect(() => {
    loadAgents();
    loadProfile();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/council/agents`, {
        withCredentials: true
      });
      setAgents(response.data.agents);
      
      // Check premium status
      const settings = await axios.get(`${API_URL}/api/ai/emulator/settings`, {
        withCredentials: true
      });
      setIsPremium(settings.data.is_premium);
    } catch (error) {
      console.error('Error loading agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const [profileRes, suggestionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/council/memory/profile`, { withCredentials: true }),
        axios.get(`${API_URL}/api/council/memory/suggestions`, { withCredentials: true })
      ]);
      setAuthorProfile(profileRes.data);
      setSuggestions(suggestionsRes.data);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const runWorkflow = async () => {
    if (!selectedWorkflow || !workflowContent.trim()) {
      toast.error('Selecione um workflow e insira o conteúdo');
      return;
    }

    setProcessing(true);
    setWorkflowResult(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/council/workflow`, {
        workflow_type: selectedWorkflow,
        content: workflowContent
      }, { withCredentials: true });

      setWorkflowResult(response.data);
      toast.success('Workflow concluído!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar workflow');
    } finally {
      setProcessing(false);
    }
  };

  const consultCouncil = async () => {
    if (!question.trim()) {
      toast.error('Digite uma pergunta');
      return;
    }

    setProcessing(true);
    setCouncilResponse(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/council/consult`, {
        question,
        context: questionContext
      }, { withCredentials: true });

      setCouncilResponse(response.data);
      toast.success('Conselho consultado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao consultar conselho');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8" data-testid="council-page">
        <div className="max-w-5xl mx-auto space-y-8">
          
          {/* Header */}
          <div>
            <h1 className="text-3xl md:text-4xl font-light mb-2 flex items-center gap-3">
              <Users className="text-amber-500" />
              Conselho Literário
            </h1>
            <p className="text-muted-foreground">
              Seu time editorial inteligente para apoiar sua escrita
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 border-b border-border pb-4">
            {[
              { id: 'agents', label: 'Agentes', icon: Users },
              { id: 'workflows', label: 'Workflows', icon: Workflow },
              { id: 'consult', label: 'Consultar', icon: MessageSquare },
              { id: 'memory', label: 'Memória', icon: Brain },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                    : 'hover:bg-muted'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab: Agents */}
          {activeTab === 'agents' && (
            <div className="grid md:grid-cols-2 gap-4">
              {agents.map((agent) => {
                const IconComponent = AGENT_ICONS[agent.id] || Users;
                return (
                  <div
                    key={agent.id}
                    className="p-5 rounded-xl border border-border bg-card hover:border-amber-500/50 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                        <IconComponent size={24} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{agent.name}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{agent.role}</p>
                        <div className="mt-3 flex flex-wrap gap-1">
                          {agent.capabilities.slice(0, 3).map((cap, i) => (
                            <span key={i} className="text-xs px-2 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300">
                              {cap}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tab: Workflows */}
          {activeTab === 'workflows' && (
            <div className="space-y-6">
              {/* Workflow Selection */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {WORKFLOWS.map((wf) => (
                  <button
                    key={wf.id}
                    onClick={() => {
                      if (wf.premium && !isPremium) {
                        toast.error('Workflow exclusivo para Premium');
                        return;
                      }
                      setSelectedWorkflow(wf.id);
                    }}
                    disabled={wf.premium && !isPremium}
                    className={`p-4 rounded-xl border text-left transition-all relative ${
                      selectedWorkflow === wf.id
                        ? 'border-amber-500 bg-amber-500/10'
                        : wf.premium && !isPremium
                        ? 'border-border opacity-50'
                        : 'border-border hover:border-amber-500/50'
                    }`}
                  >
                    {wf.premium && (
                      <Crown size={16} className="absolute top-3 right-3 text-amber-500" />
                    )}
                    <h4 className="font-semibold">{wf.name}</h4>
                    <p className="text-xs text-muted-foreground mt-1">{wf.description}</p>
                    <div className="flex gap-1 mt-3">
                      {wf.agents.map((a) => {
                        const Icon = AGENT_ICONS[a] || Users;
                        return <Icon key={a} size={14} className="text-amber-500" />;
                      })}
                    </div>
                  </button>
                ))}
              </div>

              {/* Content Input */}
              {selectedWorkflow && (
                <div className="space-y-4 p-6 rounded-xl border border-border bg-card">
                  <Label>Conteúdo para processar</Label>
                  <Textarea
                    placeholder="Cole seu texto aqui..."
                    className="min-h-[200px] font-serif"
                    value={workflowContent}
                    onChange={(e) => setWorkflowContent(e.target.value)}
                  />
                  <Button
                    onClick={runWorkflow}
                    disabled={processing || !workflowContent.trim()}
                    size="lg"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Executar Workflow
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Workflow Result */}
              {workflowResult && (
                <div className="p-6 rounded-xl border-2 border-green-500/50 bg-green-50/50 dark:bg-green-950/20">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <CheckCircle className="text-green-500" />
                    Resultado do Workflow
                  </h3>
                  <div className="mb-4">
                    <p className="text-sm text-muted-foreground">
                      Agentes utilizados: {workflowResult.agents_used?.join(' → ')}
                    </p>
                  </div>
                  <div className="prose prose-sm dark:prose-invert max-w-none p-4 bg-white/80 dark:bg-gray-900/80 rounded-lg border max-h-[400px] overflow-y-auto">
                    <pre className="whitespace-pre-wrap font-serif">
                      {workflowResult.final_content}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab: Consult */}
          {activeTab === 'consult' && (
            <div className="space-y-6">
              <div className="p-6 rounded-xl border border-border bg-card">
                <h3 className="font-semibold mb-4">Pergunte ao Conselho</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Faça uma pergunta e receba perspectivas de múltiplos agentes especializados.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <Label>Sua pergunta</Label>
                    <Textarea
                      placeholder="Ex: Como posso melhorar a estrutura do meu capítulo?"
                      className="mt-1"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Contexto adicional (opcional)</Label>
                    <Textarea
                      placeholder="Cole um trecho do seu texto para contextualizar..."
                      className="mt-1 font-serif"
                      value={questionContext}
                      onChange={(e) => setQuestionContext(e.target.value)}
                    />
                  </div>
                  <Button
                    onClick={consultCouncil}
                    disabled={processing || !question.trim()}
                  >
                    {processing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Consultando...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Consultar Conselho
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Council Response */}
              {councilResponse && (
                <div className="space-y-4">
                  <h3 className="font-semibold">Respostas do Conselho</h3>
                  {Object.entries(councilResponse.responses || {}).map(([agentId, data]) => {
                    const Icon = AGENT_ICONS[agentId] || Users;
                    return (
                      <div key={agentId} className="p-5 rounded-xl border border-border bg-card">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                            <Icon size={16} className="text-amber-600" />
                          </div>
                          <h4 className="font-semibold">{data.agent_name}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {data.advice}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tab: Memory */}
          {activeTab === 'memory' && (
            <div className="space-y-6">
              {/* Evolution Metrics */}
              {authorProfile?.evolution && (
                <div className="p-6 rounded-xl border border-border bg-card">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="text-amber-500" />
                    Sua Evolução
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-3xl font-bold text-amber-600">{authorProfile.evolution.writing_streak}</p>
                      <p className="text-xs text-muted-foreground">Dias escrevendo</p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-3xl font-bold text-blue-600">{authorProfile.evolution.avg_session_words}</p>
                      <p className="text-xs text-muted-foreground">Palavras/sessão</p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-3xl font-bold text-green-600">{authorProfile.evolution.consistency_score}%</p>
                      <p className="text-xs text-muted-foreground">Consistência</p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-xl font-bold capitalize">{authorProfile.evolution.improvement_trend}</p>
                      <p className="text-xs text-muted-foreground">Tendência</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Personalized Suggestions */}
              {suggestions && (
                <div className="p-6 rounded-xl border border-amber-200/50 dark:border-amber-800/30 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Lightbulb className="text-amber-500" />
                    Sugestões Personalizadas
                  </h3>
                  <div className="space-y-3">
                    {suggestions.suggestions?.map((suggestion, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/60 dark:bg-gray-800/60">
                        <Sparkles size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm">{suggestion}</p>
                      </div>
                    ))}
                  </div>
                  {suggestions.top_topics?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-amber-200/30">
                      <p className="text-xs text-muted-foreground mb-2">Seus temas frequentes:</p>
                      <div className="flex flex-wrap gap-2">
                        {suggestions.top_topics.map((topic, i) => (
                          <span key={i} className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300">
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Statistics */}
              {authorProfile?.statistics && (
                <div className="p-6 rounded-xl border border-border bg-card">
                  <h3 className="font-semibold mb-4">Estatísticas Gerais</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold">{authorProfile.statistics.total_words?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Palavras escritas</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{authorProfile.statistics.total_chapters}</p>
                      <p className="text-xs text-muted-foreground">Capítulos</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{authorProfile.statistics.total_generations}</p>
                      <p className="text-xs text-muted-foreground">Gerações IA</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
